/**
 * ProfileBanner — QA #007
 *
 * Shared banner renderer used by every public collector-profile route so both
 * `/c/{username}` and `/collectors/{slug}` present the uploaded banner
 * identically at desktop, tablet, and mobile breakpoints.
 *
 * Rendering rules (per the QA #007 spec):
 *   * Fixed responsive frame — h-40 on mobile, h-56 on md+ — same on every
 *     collector profile.
 *   * Uploaded image is centered horizontally + vertically and fully visible
 *     (never cropped, never stretched, aspect ratio preserved).
 *   * Uses `object-fit: contain` on an <img>. When the upload doesn't match
 *     the frame aspect ratio, a softly blurred + darkened copy of the same
 *     image fills the letterbox area (the "premium alternative" from the QA
 *     spec). No branded artwork ever loses its top/bottom/left/right edges.
 *   * When no banner has been uploaded, falls back to a neutral surface.
 *
 * Props:
 *   src         — resolved banner URL, or null / undefined when unset.
 *   testid      — data-testid for the banner container (defaults to
 *                 `profile-banner`). Callers can override per route so tests
 *                 can distinguish `/c/*` from `/collectors/*` if needed.
 *   className   — optional extra classes appended to the container.
 */
import React from "react";

export default function ProfileBanner({ src, testid = "profile-banner", className = "" }) {
  if (!src) {
    return (
      <div
        className={
          "w-full h-40 md:h-56 border-b-2 border-[var(--ink)] " +
          "bg-neutral-100 " + className
        }
        data-testid={testid}
        data-has-banner="false"
        aria-hidden="true"
      />
    );
  }
  return (
    <div
      className={
        "relative w-full h-40 md:h-56 border-b-2 border-[var(--ink)] " +
        "bg-neutral-100 overflow-hidden " + className
      }
      data-testid={testid}
      data-has-banner="true"
    >
      {/* Softly blurred + darkened backdrop copy fills the letterbox area so
          the frame never looks empty when the upload aspect doesn't match. */}
      <div
        className="absolute inset-0 bg-center bg-no-repeat scale-110 blur-xl brightness-75"
        style={{ backgroundImage: `url("${src}")`, backgroundSize: "cover" }}
        aria-hidden="true"
      />
      {/* The fully visible, complete-artwork copy — this is what the collector
          uploaded, rendered with no cropping and no distortion. */}
      <img
        src={src}
        alt=""
        className="relative w-full h-full object-contain object-center"
        data-testid={`${testid}-img`}
      />
    </div>
  );
}
